/**
 * Lijst van werkplek v6 (ontwerp 26, sessie 67).
 *
 * Bovenin, staat stil: vier kanaaltegels (open zaken per kanaal, klik is
 * filter), "Zoek op alles" en de mappen Open, Afgerond, Spam, Systeem. Daaronder
 * een eigen scrollgebied met één platte lijst op tijd van het laatste
 * bericht, nieuwste boven; alleen live staat daarboven (sessie 75).
 * Afgerond staat per dag.
 *
 * Live: de lijst leest uit de ene live-stroom van het scherm. Een kaart
 * die daar live is, toont "Nova antwoordt live", ook als de server nog
 * een andere groep gaf; events verversen de lijst zonder herladen.
 *
 * Motor 7 golf 2, sessie D (keuze 1 en 2 van 75, mock v10): bovenin vier
 * tegels (Voor jou, Nakijken, Wacht, Afgerond), daaronder de kanalen plus
 * Beloftes en het zoekveld; Spam en Systeem als kleine mapjes. Open wordt
 * altijd geladen: de tellers en de tegel per kaart komen daaruit, zodat een
 * kaart die het midden verstuurt of doorzet meteen van tegel wisselt. Kanaal
 * en Beloftes filteren in het scherm. Nieuw bericht staat vast onderaan.
 *
 * Sessie 72: een event over een kaart die er al staat, werkt alleen die
 * kaart bij (moment, vet); alleen een onbekende zaak (een nieuw gesprek)
 * of iets wat het event niet draagt, laat de lijst op de achtergrond
 * opnieuw ophalen. Na Sluiten, Spam en Verwijderen gaat de kaart meteen
 * naar zijn map. Naast de stroom een traag vangnet (5 min, `vangnet.ts`);
 * elke minuut als de stroom dicht is.
 *
 * 28-10 (keuze JP): bovenaan Voor jou een rij "Live nu" met de chats en
 * WhatsApps waar Nova nu live mee bezig is en die onder Wacht staan. Zo ziet
 * JP een nieuwe chat op de pc meteen. Een venster, geen verplaatsing: de
 * teller van Voor jou telt ze niet, en zonder live gesprek is de rij weg.
 */
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  MAPPEN,
  v6Api,
  VERWERK_PORTIE,
  type Kanaal,
  type Map as MapNaam,
  type Tegel,
  type V6Kaart,
  type V6Lijst,
} from "../api";
import { useV6 } from "../context";
import { Icoon } from "../Icoon";
import { lijstQuery, lijstSleutel } from "../lijstQuery";
import { useLiveEvent, useLive, type LiveEvent, type LiveSoort } from "../live";
import {
  beginMeting,
  inPaginaKlok,
  laatsteAanvraag,
  meetNaAanvraag,
  rondMetingAf,
  type LopendeMeting,
} from "../meting";
import {
  AFGEROND_DOOR_JOU,
  kaartBijEvent,
  naAfronden,
  naTerugzetten,
  planStilleVerversing,
  verplaatsKaart,
  werkKaartBij,
} from "../naActie";
import { v6Sleutels } from "../sleutels";
import { vangnetInterval } from "../vangnet";

import {
  filterNaarUrl,
  filterUitUrl,
  leesVerwerk,
  liveNu,
  samenvatting,
  splitsAfgerond,
  sorteerOpen,
  tegelNaarUrl,
  tegelUitUrl,
  tegelVan,
  telKanalen,
  telTegels,
  uitkomstOnbekend,
  verversBijKlantbericht,
  vingerafdruk,
  laatsteBericht,
} from "./hulp";
import { Kaart, type Handeling } from "./Kaart";
import { NieuwBericht } from "./NieuwBericht";
import { Proefbericht } from "./Proefbericht";
import { KanaalFilter, Tegels } from "./Tegels";
import "@/styles/statusbalk.css";
import "./lijst.css";

export interface LijstProps {
  /** JP tikt een kaart aan (op de iPhone opent dan de zaak). */
  onKies: (zaakId: string) => void;
  /** Kies de bovenste zaak. Twee momenten:
   *  - de map Open is geladen (zonder zoekterm) en er is nog niets gekozen;
   *  - JP wisselt van kanaaltegel, map of zoekterm en de gekozen zaak staat
   *    niet in de nieuwe lijst (ook Afgerond, Spam en zoeken). Een lege
   *    lijst geeft `null`: het midden zegt dan "Kies een zaak".
   *  Verdwijnt een gekozen zaak uit de lijst zonder dat JP filtert
   *  (afgerond, Nova rondde af), dan komt deze aanroep niet: de zaak
   *  blijft open in het midden, met de ongedaan-balk in de kop. */
  onEerste: (zaakId: string | null) => void;
}

const ZOEK_WACHT_MS = 250;
/** Hoe lang "3 verwerkt, 0 mislukt" blijft staan als alles lukte. */
const BEVESTIG_MS = 4_000;
const ONBEKEND = "Onbekend, lijst ververst.";
const NIET_VERSTUURD = "Niet verstuurd: een eerdere portie kreeg geen antwoord.";
const VERANDERD = "Veranderd na het aanvinken. Kijk opnieuw en vink weer aan.";
const VERVERS_MISLUKT =
  "Lijst verversen mislukt. De vinkjes blijven staan tot de lijst weer geladen is.";

/** Na het verwerken: wat de verse lijst nog moet bevestigen. */
interface NaVerwerk {
  /** Stand van de lijstquery toen het verwerken klaar was. */
  basis: number;
  geslaagd: string[];
  onbekend: string[];
}
const VERVERS_MS = 1_500;
const VERVERS_BIJ = new Set<LiveSoort>(["klantbericht", "antwoord", "concept", "status"]);
/** Een meting "nieuw bericht" waarvan de kaart na zo lang nog niet staat, vervalt. */
const NIEUW_BERICHT_VERVALT_MS = 30_000;

const MELDING: Record<Handeling, string> = {
  sluiten: "Afgerond.",
  spam: "Als spam gemarkeerd.",
  verwijderen: "Verwijderd.",
};
const MISLUKT: Record<Handeling, string> = {
  sluiten: "Sluiten mislukt.",
  spam: "Als spam markeren mislukt.",
  verwijderen: "Verwijderen mislukt.",
};

const LEEG_TEGEL: Record<Tegel, string> = {
  voor_jou: "Niets meer voor jou",
  nakijken: "Niets na te kijken",
  wacht: "Niets in wacht",
  afgerond: "Niets afgerond",
};

/** Spam en Systeem als kleine mapjes onder het zoekveld. */
const MAPJES: MapNaam[] = ["spam", "systeem"];

const LEEG: Record<MapNaam, string> = {
  open: "Niets open",
  afgerond: "Niets afgerond",
  spam: "Geen spam",
  systeem: "Geen systeemmail",
};

interface Melding {
  soort: Handeling;
  zaakId: string;
  eventId: string | null;
  tot: number;
}

/** Meting "nieuw bericht": vanaf `op` van de server, met de tijd op de stroom. */
function meetBericht(event: LiveEvent): LopendeMeting | null {
  const op = inPaginaKlok(event.op);
  if (op === null) return null;
  const meting = beginMeting("nieuw_bericht", op);
  meting.stappen.live = performance.now() - op;
  return meting;
}

/** De reden van de server, als die er is ("Zaak is al afgerond"). */
function serverReden(fout: unknown): string {
  const detail = (fout as { response?: { data?: { detail?: unknown } } } | null)?.response?.data
    ?.detail;
  return typeof detail === "string" && detail ? ` ${detail}` : "";
}

export function Lijst({ onKies, onEerste }: LijstProps) {
  const { zaakId, zetLijstFilter } = useV6();
  const live = useLive();
  const qc = useQueryClient();

  // Sessie 70: tegel en map staan in de url, zodat ze na F5 blijven. De
  // gekozen zaak (`?zaak=`) en een pushmelding (`?chat=`, `?mail=`) staan
  // er ook; die laten we staan.
  const [params, setParams] = useSearchParams();
  const { map, kanaal } = filterUitUrl(params);
  const tegel = tegelUitUrl(params, map);
  const beloftes = params.get("belofte") === "1";
  const [invoer, setInvoer] = useState("");
  const [zoek, setZoek] = useState("");
  const [uitgeklapt, setUitgeklapt] = useState<Record<string, boolean>>({});
  const [melding, setMelding] = useState<Melding | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  // Sessie 53: aanvinken en in bulk verwerken. Per vinkje bewaren we wat
  // de kaart zei bij het aanvinken (zie `vingerafdruk`).
  const [aangevinkt, setAangevinkt] = useState<Map<string, string>>(() => new Map());
  const [vinkFouten, setVinkFouten] = useState<Record<string, string>>({});
  const [verwerkt, setVerwerkt] = useState<Set<string>>(() => new Set());
  const [verwerkBezig, setVerwerkBezig] = useState(false);
  const [bevestiging, setBevestiging] = useState<{ tekst: string; alleGoed: boolean } | null>(
    null,
  );
  const [naVerwerk, setNaVerwerk] = useState<NaVerwerk | null>(null);
  const [verversFout, setVerversFout] = useState(false);

  // Zoeken wacht tot JP even stopt met typen.
  useEffect(() => {
    const t = setTimeout(() => setZoek(invoer.trim()), ZOEK_WACHT_MS);
    return () => clearTimeout(t);
  }, [invoer]);

  // Het midden leest de stand van een kaart uit dezelfde query.
  // Kanaal filtert in het scherm (sessie D): de server geeft alles van de map.
  useEffect(() => {
    zetLijstFilter({ map, kanaal: null, zoek });
  }, [zetLijstFilter, map, zoek]);

  // Open altijd, voor de tegels (ook als Afgerond, Spam of Systeem in beeld is).
  const basisVraag = useQuery({
    ...lijstQuery({ map: "open", kanaal: null, zoek: "" }),
    refetchInterval: vangnetInterval(live.verbonden),
  });
  const basis = basisVraag.data;

  const vraag = useQuery({
    ...lijstQuery({ map, kanaal: null, zoek }),
    // Traag vangnet bij een open stroom, elke minuut als hij dicht is (vangnet.ts).
    // Bij Open zonder zoekterm is dit dezelfde query als de basis: één vangnet.
    refetchInterval: map === "open" && !zoek ? false : vangnetInterval(live.verbonden),
    // Tijdens typen of filteren blijft de vorige lijst staan, maar nooit
    // die van een andere map.
    placeholderData: (vorige: V6Lijst | undefined) =>
      vorige && vorige.map === map ? keepPreviousData(vorige) : undefined,
  });
  const data = vraag.data;

  // Een onbekende zaak (nieuw gesprek): de lijst meteen opnieuw, samengevoegd:
  // hoogstens eens per 1,5 s.
  const laatste = useRef(0);
  const gepland = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ververs = useCallback(() => {
    const wacht = laatste.current + VERVERS_MS - Date.now();
    if (wacht <= 0) {
      laatste.current = Date.now();
      void qc.invalidateQueries({ queryKey: v6Sleutels.lijst });
      return;
    }
    if (gepland.current) return;
    gepland.current = setTimeout(() => {
      gepland.current = null;
      laatste.current = Date.now();
      void qc.invalidateQueries({ queryKey: v6Sleutels.lijst });
    }, wacht);
  }, [qc]);
  useEffect(
    () => () => {
      if (gepland.current) clearTimeout(gepland.current);
    },
    [],
  );
  /** Staat deze zaak in een Open-lijst in de cache? */
  const kaartInOpen = (id: string): boolean =>
    qc
      .getQueriesData<V6Lijst>({ queryKey: v6Sleutels.lijst })
      .some(([, d]) => d?.map === "open" && d.kaarten.some((k) => k.zaak_id === id));

  // Nieuw bericht in de lijst: meten van `op` (server) tot het frame.
  const nieuwBericht = useRef(new Map<string, { meting: LopendeMeting; tot: number }>());
  /** Metingen waarvan de kaart na 30 s nog niet staat, tellen niet meer. */
  const ruimNieuwBerichtOp = () => {
    const nu = Date.now();
    for (const [id, { tot }] of nieuwBericht.current) if (tot <= nu) nieuwBericht.current.delete(id);
  };
  useLiveEvent((event) => {
    // Sessie 72: een mislukte verzending zet de kaart terug in Open (dat
    // rekent de server uit); ook als hij vanaf een ander scherm verstuurd is.
    if (event.soort === "verzendstatus" && event.verzendstatus === "mislukt" && event.zaak_id) {
      planStilleVerversing(qc, 0);
      return;
    }
    if (!VERVERS_BIJ.has(event.soort)) return;
    const kaart = kaartBijEvent(qc, event);
    const meting = event.soort === "klantbericht" ? meetBericht(event) : null;
    if (!kaart) {
      // Een klantbericht van een onbekende zaak is een nieuw gesprek: meteen
      // ophalen. Iets anders over een kaart die hier niet (meer) staat, zoals
      // de status na eigen Sluiten, gaat mee met de stille herlading.
      if (event.soort === "klantbericht") {
        ruimNieuwBerichtOp();
        if (meting && event.zaak_id) {
          nieuwBericht.current.set(event.zaak_id, { meting, tot: Date.now() + NIEUW_BERICHT_VERVALT_MS });
        }
        ververs();
      } else {
        planStilleVerversing(qc);
      }
      return;
    }
    const status = event.soort === "status" ? (event.extra?.status ?? event.extra?.stand) : null;
    if (status === "afgerond") {
      void verplaatsKaart(qc, kaart.zaak_id, { map: "afgerond", woord: AFGEROND_DOOR_JOU });
    } else if (event.soort === "klantbericht" || event.soort === "antwoord") {
      // Wat het event draagt: het moment, en vet bij een klantbericht.
      const wasOpen = kaartInOpen(kaart.zaak_id);
      werkKaartBij(qc, kaart.zaak_id, {
        moment: event.op,
        ...(event.soort === "klantbericht" ? { nieuw: true } : {}),
      });
      // Stond de kaart niet in Open of niet in Voor jou (Wacht, Afgerond,
      // Nakijken), dan kan dit bericht zijn tegel veranderen: de server rekent
      // hem meteen opnieuw uit (sessie 161, recensie 76 punt 2).
      if (event.soort === "klantbericht" && verversBijKlantbericht(kaart, wasOpen)) ververs();
      // Na een antwoord verandert de stand (live, wacht op klant); dat rekent
      // de server uit, gebundeld met andere events.
      else if (event.soort === "antwoord") planStilleVerversing(qc);
    } else {
      // Concept of een andere stand: dat rekent de server uit.
      planStilleVerversing(qc);
    }
    if (meting) rondMetingAf(meting);
  });

  // Een nieuwe kaart uit een live-event staat er: meting rond.
  useEffect(() => {
    if (!data || nieuwBericht.current.size === 0) return;
    ruimNieuwBerichtOp();
    for (const k of data.kaarten) {
      const meting = nieuwBericht.current.get(k.zaak_id)?.meting;
      if (!meting) continue;
      nieuwBericht.current.delete(k.zaak_id);
      const aanvraag = laatsteAanvraag(/\/api\/werkplek\/v6\/lijst$/);
      rondMetingAf(meting, {
        lijst: aanvraag ? aanvraag.responseEnd - aanvraag.startTime : undefined,
      });
    }
  }, [data]);

  const nu = new Date();
  /** Kanaal of Beloftes, in het scherm. */
  const pastFilter = useCallback(
    (k: V6Kaart) => (kanaal ? k.kanaal === kanaal : !beloftes || Boolean(k.belofte_voor)),
    [kanaal, beloftes],
  );
  // De open kaarten (zonder net verwerkte) en de tellers van de tegels.
  const openKaarten = useMemo(
    () => (basis?.kaarten ?? []).filter((k) => !verwerkt.has(k.zaak_id)),
    [basis, verwerkt],
  );
  const tegelTellers = basis ? telTegels(openKaarten, basis.afgerond_vandaag ?? 0) : null;
  // Afgerond: wat beantwoord is en alleen nog op de klant wacht (JP 14-10).
  const beantwoord = useMemo(
    () => (tegel === "afgerond" && !zoek ? openKaarten.filter((k) => tegelVan(k) === "afgerond") : []),
    [openKaarten, tegel, zoek],
  );
  // De kaarten van de gekozen tegel, voor kanaal en Beloftes.
  const inTegel = useMemo<V6Kaart[]>(() => {
    if (zoek) return [];
    if (tegel === "afgerond") return [...beantwoord, ...(data?.map === "afgerond" ? data.kaarten : [])];
    if (tegel) return openKaarten.filter((k) => tegelVan(k) === tegel);
    return data && data.map === map ? data.kaarten : [];
  }, [zoek, tegel, beantwoord, data, map, openKaarten]);
  const kanaalTellers = basis || data ? telKanalen(inTegel) : null;
  // Net verwerkte kaarten verdwijnen meteen, ook voor de lijst ververst is.
  const open = useMemo(
    () =>
      zoek
        ? data
          ? sorteerOpen(
              data.kaarten.filter(
                (k) => !verwerkt.has(k.zaak_id) && (k.map ?? "open") === "open" && pastFilter(k),
              ),
              live.isLive,
            )
          : []
        : map === "open" && tegel !== "afgerond"
          ? sorteerOpen(inTegel.filter(pastFilter), live.isLive)
          : [],
    [data, live.isLive, verwerkt, zoek, map, tegel, inTegel, pastFilter],
  );
  // Rij "Live nu" bovenaan Voor jou (28-10): een venster op wat Nova nu live
  // doet; de kaarten blijven onder Wacht en tellen niet mee in Voor jou.
  const liveRij = useMemo(
    () =>
      !zoek && map === "open" && tegel === "voor_jou"
        ? liveNu(openKaarten.filter(pastFilter), live.isLive)
        : [],
    [zoek, map, tegel, openKaarten, pastFilter, live.isLive],
  );
  const beantwoordGetoond = useMemo(
    () => sorteerOpen(beantwoord.filter(pastFilter), live.isLive),
    [beantwoord, pastFilter, live.isLive],
  );
  // Zoeken gaat over alle mappen (sessie 82): de gevonden zaken buiten Open.
  const zoekDicht = useMemo(
    () =>
      data && zoek && !vraag.isPlaceholderData
        ? data.kaarten.filter((k) => (k.map ?? "open") !== "open" && pastFilter(k))
        : [],
    [data, zoek, vraag.isPlaceholderData, pastFilter],
  );
  // Vinkbaar in beeld: een kaart die live werd, telt niet meer mee.
  const vinkbare = useMemo(
    () => open.filter((o) => o.kaart.vinkbaar && o.groep !== "live").map((o) => o.kaart.zaak_id),
    [open],
  );
  // Een kaart met onbekende uitkomst gaat niet nog eens weg voor de verse
  // lijst er is: dat kon een tweede bericht aan de klant worden.
  const teVerwerken = vinkbare.filter(
    (id) => aangevinkt.has(id) && !naVerwerk?.onbekend.includes(id),
  );
  const allesAan = vinkbare.length > 0 && teVerwerken.length === vinkbare.length;

  // Sessie 161 (recensie 76, punt 3): Vandaag telt mee in de tegel, Eerder niet.
  const afgerondDelen = useMemo(
    () =>
      data && data.map === "afgerond" && !zoek
        ? splitsAfgerond(data.kaarten.filter(pastFilter), new Date())
        : { vandaag: [], eerder: [] },
    [data, zoek, pastFilter],
  );
  const dagen = afgerondDelen.eerder;
  // De kaarten in de volgorde waarin het scherm ze toont.
  const getoond = useMemo<string[]>(() => {
    if (!data) return [];
    if (zoek) return [...open.map((o) => o.kaart.zaak_id), ...zoekDicht.map((k) => k.zaak_id)];
    if (data.map === "open") return open.map((o) => o.kaart.zaak_id);
    if (data.map === "afgerond")
      return [
        ...beantwoordGetoond.map((o) => o.kaart.zaak_id),
        ...afgerondDelen.vandaag.map((k) => k.zaak_id),
        ...dagen.flatMap((d) => d.kaarten.map((k) => k.zaak_id)),
      ];
    return data.kaarten.filter(pastFilter).map((k) => k.zaak_id);
  }, [data, open, dagen, afgerondDelen, zoek, zoekDicht, beantwoordGetoond, pastFilter]);

  // Wissel van kanaaltegel, map of zoekterm: die lijst moet nog getoetst
  // worden zodra zijn eigen data er is (niet de placeholder van de vorige).
  const filterSleutel = `${map}|${tegel ?? ""}|${kanaal ?? ""}|${beloftes ? "b" : ""}|${zoek}`;
  const vorigeSleutel = useRef(filterSleutel);
  const teToetsen = useRef<string | null>(null);
  useEffect(() => {
    if (vorigeSleutel.current === filterSleutel) return;
    vorigeSleutel.current = filterSleutel;
    teToetsen.current = filterSleutel;
  }, [filterSleutel]);

  // Bovenste zaak kiezen bij het eerste laden, als er nog niets gekozen is,
  // in elke map en bij elk kanaalfilter (sessie 82: nooit "Kies een zaak"
  // bij een gevulde lijst). Een gekozen zaak die uit de lijst verdwijnt
  // blijft staan.
  useEffect(() => {
    if (zaakId) return;
    if (teToetsen.current === filterSleutel) return;
    if (!data || vraag.isPlaceholderData || data.map !== map) return;
    const boven = getoond[0] ?? null;
    if (boven) onEerste(boven);
  }, [data, vraag.isPlaceholderData, map, zaakId, getoond, onEerste, filterSleutel]);

  // Na een wissel: staat de gekozen zaak niet in de nieuwe lijst, dan
  // springt de keuze naar de bovenste kaart (of naar niets bij een lege
  // lijst). Een handeling of een live-event verandert de sleutel niet,
  // dus een net afgeronde zaak blijft staan met de ongedaan-balk.
  useEffect(() => {
    if (teToetsen.current !== filterSleutel) return;
    if (!data || vraag.isPlaceholderData || vraag.isError || data.map !== map) return;
    teToetsen.current = null;
    if (zaakId && getoond.includes(zaakId)) return;
    const boven = getoond[0] ?? null;
    if (boven === zaakId) return;
    onEerste(boven);
  }, [data, vraag.isPlaceholderData, vraag.isError, map, filterSleutel, zaakId, getoond, onEerste]);

  // De ongedaan-balk verdwijnt als de termijn verstreken is.
  useEffect(() => {
    if (!melding) return;
    const t = setTimeout(() => setMelding(null), Math.max(0, melding.tot - Date.now()));
    return () => clearTimeout(t);
  }, [melding]);

  const handel = async (soort: Handeling, id: string) => {
    setBezig(true);
    try {
      const uit = await v6Api[soort](id);
      const tot = Date.parse(uit.ongedaan_tot);
      setFout(null);
      setMelding({
        soort,
        zaakId: id,
        eventId: uit.event_id,
        tot: Number.isNaN(tot) ? Date.now() + 10_000 : tot,
      });
      // Sessie 72: de kaart meteen naar zijn map; zaak en lijst stil opnieuw.
      void naAfronden(qc, id, soort);
      meetNaAanvraag("sluiten", /\/afronden$/);
    } catch (e) {
      setFout(`${MISLUKT[soort]}${serverReden(e)}`);
    } finally {
      setBezig(false);
    }
  };

  const ongedaan = async () => {
    if (!melding?.eventId) return;
    setBezig(true);
    try {
      await v6Api.ongedaan(melding.zaakId, melding.eventId);
      setFout(null);
      setMelding(null);
      naTerugzetten(qc, melding.zaakId);
    } catch (e) {
      setFout(`Ongedaan maken mislukt.${serverReden(e)}`);
    } finally {
      setBezig(false);
    }
  };

  // De samenvatting verdwijnt vanzelf als alles lukte; anders blijft hij.
  useEffect(() => {
    if (!bevestiging?.alleGoed) return;
    const t = setTimeout(() => setBevestiging(null), BEVESTIG_MS);
    return () => clearTimeout(t);
  }, [bevestiging]);

  const kaartVan = useMemo(
    () => new Map((data?.kaarten ?? []).map((k) => [k.zaak_id, k])),
    [data],
  );

  /** Haal een reden weg bij deze kaarten, alleen als het die reden is. */
  const wisReden = useCallback(
    (ids: string[], reden: string) =>
      setVinkFouten((oud) => {
        if (!ids.some((id) => oud[id] === reden)) return oud;
        const nieuw = { ...oud };
        for (const id of ids) if (nieuw[id] === reden) delete nieuw[id];
        return nieuw;
      }),
    [],
  );

  const vink = (id: string, aan: boolean) => {
    setAangevinkt((oud) => {
      const nieuw = new Map(oud);
      const k = kaartVan.get(id);
      if (aan && k) nieuw.set(id, vingerafdruk(k));
      else nieuw.delete(id);
      return nieuw;
    });
    if (aan) wisReden([id], VERANDERD);
  };

  const allesOpDezePagina = () =>
    setAangevinkt((oud) => {
      const nieuw = new Map(oud);
      for (const id of vinkbare) {
        const k = kaartVan.get(id);
        if (allesAan || !k) nieuw.delete(id);
        else nieuw.set(id, vingerafdruk(k));
      }
      return nieuw;
    });

  // Vinkjes volgen de lijst die er nu staat (sessie 53, punt 10):
  // - een kaart die na het aanvinken veranderde, verliest zijn vinkje, met
  //   een regel waarom;
  // - vinkjes op kaarten buiten de huidige map of filter vallen weg;
  // - een kaart die net goed verwerkt is, is uit de selectie: hij valt weg
  //   zonder regel. Hij verandert natuurlijk (nu "Verstuurd"), maar dat is
  //   geen reden om opnieuw te kijken.
  // Tijdens het verwerken niet: dan beslist de uitkomst.
  useEffect(() => {
    if (verwerkBezig || !data || vraag.isPlaceholderData || data.map !== map) return;
    if (aangevinkt.size === 0) return;
    const geslaagd = new Set([...verwerkt, ...(naVerwerk?.geslaagd ?? [])]);
    const veranderd: string[] = [];
    const nieuw = new Map<string, string>();
    for (const [id, afdruk] of aangevinkt) {
      const k = kaartVan.get(id);
      if (!k || geslaagd.has(id)) continue;
      if (vingerafdruk(k) !== afdruk) veranderd.push(id);
      else nieuw.set(id, afdruk);
    }
    if (nieuw.size === aangevinkt.size) return;
    setAangevinkt(nieuw);
    if (veranderd.length > 0) {
      setVinkFouten((oud) => {
        const bij = { ...oud };
        for (const id of veranderd) if (!bij[id]) bij[id] = VERANDERD;
        return bij;
      });
    }
  }, [
    verwerkBezig,
    data,
    vraag.isPlaceholderData,
    map,
    aangevinkt,
    kaartVan,
    verwerkt,
    naVerwerk,
  ]);

  // De verse lijst is er: nu pas de selectie legen (punt 9). Wat verwerkt
  // is, staat er niet meer in; een onbekende uitkomst is nu bekend.
  // Het aantal geslaagde laadbeurten van deze lijst; `isFetching` lezen we
  // mee zodat het einde van elke laadbeurt een render geeft.
  const leesTeller = () =>
    qc.getQueryState(lijstSleutel({ map, kanaal: null, zoek }))?.dataUpdateCount ?? 0;
  const dataTeller = leesTeller();
  const laadt = vraag.isFetching;
  useEffect(() => {
    if (!naVerwerk || laadt || dataTeller <= naVerwerk.basis || vraag.isPlaceholderData) return;
    const weg = new Set([...naVerwerk.geslaagd, ...naVerwerk.onbekend]);
    setAangevinkt((oud) => new Map([...oud].filter(([id]) => !weg.has(id))));
    wisReden(naVerwerk.onbekend, ONBEKEND);
    setVerwerkt(new Set());
    setVerversFout(false);
    setNaVerwerk(null);
  }, [naVerwerk, laadt, dataTeller, vraag.isPlaceholderData, wisReden]);

  const verwerk = async () => {
    const ids = teVerwerken;
    if (ids.length === 0) return;
    setVerwerkBezig(true);
    setBevestiging(null);
    setVerversFout(false);
    const geslaagd: string[] = [];
    const fouten: Record<string, string> = {};
    const onbekend: string[] = [];
    let nietVerstuurd = 0;
    // Porties van 10 met een eigen, ruime timeout. Komt er van een portie
    // geen antwoord, dan weten we niet wat er gebeurde: die kaarten zijn
    // "onbekend" tot de verse lijst er is, en de rest gaat niet meer weg
    // (met een regel per kaart, nooit stil overgeslagen).
    for (let i = 0; i < ids.length; i += VERWERK_PORTIE) {
      const portie = ids.slice(i, i + VERWERK_PORTIE);
      if (onbekend.length > 0) {
        for (const id of portie) fouten[id] = NIET_VERSTUURD;
        nietVerstuurd += portie.length;
        continue;
      }
      try {
        const uit = leesVerwerk(portie, await v6Api.verwerk(portie));
        geslaagd.push(...uit.geslaagd);
        Object.assign(fouten, uit.fouten);
      } catch (e) {
        if (uitkomstOnbekend(e)) {
          onbekend.push(...portie);
        } else {
          const reden = `Verwerken mislukt.${serverReden(e)}`;
          for (const id of portie) fouten[id] = reden;
        }
      }
    }
    for (const id of onbekend) fouten[id] = ONBEKEND;
    const mislukt = Object.keys(fouten).length - onbekend.length - nietVerstuurd;
    setVerwerkt((oud) => new Set([...oud, ...geslaagd]));
    setVinkFouten((oud) => {
      const nieuw: Record<string, string> = {};
      for (const [id, reden] of Object.entries(oud)) {
        if (!ids.includes(id)) nieuw[id] = reden;
      }
      return { ...nieuw, ...fouten };
    });
    setBevestiging({
      tekst: samenvatting({ verwerkt: geslaagd.length, mislukt, onbekend: onbekend.length, nietVerstuurd }),
      alleGoed: geslaagd.length === ids.length,
    });
    setNaVerwerk({ basis: leesTeller(), geslaagd, onbekend });
    setVerwerkBezig(false);
    const vers = await vraag.refetch();
    if (vers.isError) setVerversFout(true);
    // Sessie 72: de open zaak als die verwerkt is, en de menutellers; niet
    // meer alles onder ["v6"]. De lijst is net geladen.
    if (zaakId && geslaagd.includes(zaakId)) {
      void qc.invalidateQueries({ queryKey: v6Sleutels.zaak(zaakId), exact: true });
    }
    void qc.invalidateQueries({ queryKey: v6Sleutels.menutellers });
  };

  const kiesKanaal = (k: Kanaal) =>
    setParams(
      (oud) => {
        const nieuw = filterNaarUrl(oud, { kanaal: kanaal === k ? null : k });
        nieuw.delete("belofte");
        return nieuw;
      },
      { replace: true },
    );
  const kiesBeloftes = () =>
    setParams(
      (oud) => {
        const nieuw = filterNaarUrl(oud, { kanaal: null });
        if (beloftes) nieuw.delete("belofte");
        else nieuw.set("belofte", "1");
        return nieuw;
      },
      { replace: true },
    );
  const kiesTegel = (t: Tegel) => setParams((oud) => tegelNaarUrl(oud, t), { replace: true });
  const kiesMapje = (m: MapNaam) =>
    setParams(
      (oud) => {
        const nieuw = filterNaarUrl(oud, { map: map === m ? "open" : m, kanaal: null });
        nieuw.delete("tegel");
        nieuw.delete("belofte");
        return nieuw;
      },
      { replace: true },
    );

  const leeg =
    data && !vraag.isError && getoond.length === 0
      ? zoek
        ? "Niets gevonden"
        : kanaal || beloftes
          ? "Niets met dit filter"
          : tegel
            ? LEEG_TEGEL[tegel]
            : LEEG[map]
      : null;

  return (
    <section className="v6-vlak v6-lijst" aria-label="Berichten">
      <div className="v6-lijst-bovenin vo-statusbalkvrij" data-testid="lijst-bovenin">
        <Tegels tellers={tegelTellers} gekozen={zoek ? null : tegel} onKies={kiesTegel} />
        <KanaalFilter
          tellers={kanaalTellers}
          kanaal={kanaal}
          beloftes={beloftes}
          liveKanalen={live.liveKanalen}
          onKanaal={kiesKanaal}
          onBeloftes={kiesBeloftes}
        />
        <label className="v6-zoek">
          <Icoon naam="zoek" klein />
          <input
            type="search"
            placeholder="Zoek op alles"
            aria-label="Zoek op alles"
            value={invoer}
            onChange={(e) => setInvoer(e.target.value)}
          />
        </label>
        <div className="v6-mapjes" role="group" aria-label="Mappen">
          {MAPJES.map((m) => (
            <button
              key={m}
              type="button"
              className={map === m ? "on" : undefined}
              aria-pressed={map === m}
              onClick={() => kiesMapje(m)}
            >
              {MAPPEN.find((x) => x.map === m)?.woord}
            </button>
          ))}
        </div>
      </div>

      <div className="v6-scrol" data-map={map}>
        {fout ? (
          <div className="v6-fout v6-fout-melding" role="alert">
            {fout}
          </div>
        ) : null}
        {vraag.isError ? (
          <div className="v6-fout v6-fout-melding" role="alert">
            Lijst laden mislukt.{serverReden(vraag.error)}
          </div>
        ) : null}

        {map === "open" && bevestiging ? (
          <div
            className={`v6-bevestiging${bevestiging.alleGoed ? "" : " met-fout"}`}
            role="status"
          >
            {bevestiging.tekst}
          </div>
        ) : null}
        {verversFout ? (
          <div className="v6-fout v6-fout-melding" role="alert">
            {VERVERS_MISLUKT}
          </div>
        ) : null}

        {liveRij.length > 0 && data?.map === "open" ? (
          <div className="v6-dag v6-livenu" data-testid="live-nu">
            <div className="v6-dagkop">Live nu</div>
            {liveRij.map(({ kaart, groep }) => (
              <Kaart
                key={kaart.zaak_id}
                kaart={kaart}
                groep={groep}
                gekozen={kaart.zaak_id === zaakId}
                nu={nu}
                onKies={onKies}
                laatste={laatsteBericht(live.rijen, kaart.gesprek_ids)}
              />
            ))}
          </div>
        ) : null}

        {map === "open" && data?.map === "open" && vinkbare.length > 0 ? (
          <div className="v6-verwerkbalk">
            <button
              type="button"
              className="v6-knop zacht"
              aria-pressed={allesAan}
              disabled={verwerkBezig}
              onClick={allesOpDezePagina}
            >
              Alles op deze pagina
            </button>
            <button
              type="button"
              className="v6-knop acc"
              disabled={verwerkBezig || teVerwerken.length === 0}
              onClick={() => void verwerk()}
            >
              Verwerk <span className="num">{teVerwerken.length}</span> aangevinkte
            </button>
          </div>
        ) : null}

        {(zoek ? data?.map === map : map === "open" && data?.map === "open")
          ? open.map(({ kaart, groep, liveOverschreven }) => (
              <Kaart
                mapWoord={zoek ? "Open" : null}
                key={kaart.zaak_id}
                kaart={kaart}
                groep={groep}
                liveOverschreven={liveOverschreven}
                gekozen={kaart.zaak_id === zaakId}
                bezig={bezig || verwerkBezig}
                nu={nu}
                onKies={onKies}
                onHandel={(soort, id) => void handel(soort, id)}
                vinkbaar={Boolean(kaart.vinkbaar) && groep !== "live"}
                aangevinkt={aangevinkt.has(kaart.zaak_id)}
                onVink={vink}
                vinkFout={vinkFouten[kaart.zaak_id] ?? null}
                laatste={groep === "live" ? laatsteBericht(live.rijen, kaart.gesprek_ids) : null}
              />
            ))
          : null}

        {zoek && data?.map === map
          ? zoekDicht.map((kaart) => (
              <Kaart
                key={kaart.zaak_id}
                kaart={kaart}
                groep={kaart.groep}
                gekozen={kaart.zaak_id === zaakId}
                klaar
                nu={nu}
                onKies={onKies}
                mapWoord={MAPPEN.find((m) => m.map === kaart.map)?.woord ?? "Afgerond"}
              />
            ))
          : null}

        {!zoek && tegel === "afgerond" && beantwoordGetoond.length > 0 ? (
          <div className="v6-dag">
            <div className="v6-dagkop">Beantwoord, wacht op klant</div>
            {beantwoordGetoond.map(({ kaart, groep }) => (
              <Kaart
                key={kaart.zaak_id}
                kaart={kaart}
                groep={groep}
                gekozen={kaart.zaak_id === zaakId}
                nu={nu}
                onKies={onKies}
              />
            ))}
          </div>
        ) : null}

        {!zoek && map === "afgerond" && data?.map === "afgerond" ? (
          <>
            {afgerondDelen.vandaag.length > 0 ? (
              <div className="v6-dag" data-testid="afgerond-vandaag">
                <div className="v6-dagkop">Vandaag</div>
                {afgerondDelen.vandaag.map((kaart) => (
                  <Kaart
                    key={kaart.zaak_id}
                    kaart={kaart}
                    groep={kaart.groep}
                    gekozen={kaart.zaak_id === zaakId}
                    klaar
                    nu={nu}
                    onKies={onKies}
                  />
                ))}
              </div>
            ) : null}
            {dagen.length > 0 ? (
              <div className="v6-dagkop v6-eerder" data-testid="afgerond-eerder">
                Eerder
              </div>
            ) : null}
            {dagen.map((dag) => {
              // Eerdere dagen staan dicht, behalve de dag van de gekozen zaak.
              const standaard = dag.kaarten.some((k) => k.zaak_id === zaakId);
              const isOpen = (uitgeklapt[dag.sleutel] ?? standaard);
              return (
                <div key={dag.sleutel} className="v6-dag">
                  <button
                    type="button"
                    className="v6-dagkop"
                    aria-expanded={isOpen}
                    onClick={() =>
                      setUitgeklapt((oud) => ({ ...oud, [dag.sleutel]: !isOpen }))
                    }
                  >
                    {dag.kop}
                  </button>
                  {isOpen
                    ? dag.kaarten.map((kaart) => (
                        <Kaart
                          key={kaart.zaak_id}
                          kaart={kaart}
                          groep={kaart.groep}
                          gekozen={kaart.zaak_id === zaakId}
                          klaar
                          nu={nu}
                          onKies={onKies}
                        />
                      ))
                    : null}
                </div>
              );
            })}
            <div className="v6-dagkop v6-ouder">Ouder: zoek op naam, e-mail of ordernummer</div>
          </>
        ) : null}

        {!zoek && (map === "spam" || map === "systeem") && data?.map === map
          ? data.kaarten.filter(pastFilter).map((kaart) => (
              <Kaart
                key={kaart.zaak_id}
                kaart={kaart}
                groep={kaart.groep}
                gekozen={kaart.zaak_id === zaakId}
                klaar
                nu={nu}
                onKies={onKies}
              />
            ))
          : null}

        {leeg ? <p className="v6-leeg">{leeg}</p> : null}
      </div>

      <div className="v6-lijst-onderin">
        <NieuwBericht />
        {/* Sessie 158: alleen op staging (de server geeft anders 404). */}
        <Proefbericht />
      </div>

      {melding ? (
        <div className="v6-ongedaan" role="status">
          <span>{MELDING[melding.soort]}</span>
          {melding.eventId ? (
            <button type="button" disabled={bezig} onClick={() => void ongedaan()}>
              Ongedaan maken
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
